import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthProvider'
import { api } from '@/lib/api'
import { formatMoney } from '@/lib/utils'
import { CheckIcon } from '@/components/shared/Icons'
import AdminCharts from '@/features/admin/AdminCharts'

// ─── Helpers ────────────────────────────────────────────────────────────────

function getSaludo() {
  const h = new Date().getHours()
  if (h >= 5 && h < 12) return 'Buenos dias'
  if (h >= 12 && h < 20) return 'Buenas tardes'
  return 'Buenas noches'
}

function formatFechaHoy() {
  return new Date().toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })
}

function formatHora(iso: string) {
  return new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
}

function formatFechaCorta(iso: string) {
  return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })
}

// ─── Types ────────────────────────────────────────────────────────────────────

type DashboardApiData = {
  ventas: { ventas_hoy: string; total_ventas_hoy: string }
  clientes: { con_deuda: string; deuda_total: string }
  alertas: Array<{ id: string; nombre: string; stock_minimo: number; stock_total: string }>
  movimientos: Array<{
    id: string; numero: number; total: number; tipo: string
    estado: string; created_at: string; cliente_nombre: string
  }>
}

type AlertaRow = {
  id: string; nombre: string; marca: string | null; stock_minimo: number; stockTotal: number
}

type VentaHoyRow = {
  id: string; numero: number; tipo: string; total: number; estado: string
  created_at: string; clientes: { nombre: string } | null
}

// ─── Queries ─────────────────────────────────────────────────────────────────

function useDashboard() {
  return useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.get<DashboardApiData>('/api/dashboard/stats'),
    refetchInterval: 30_000,
  })
}

function useCajaActual() {
  return useQuery({
    queryKey: ['dashboard-caja'],
    queryFn: async () => {
      try {
        return await api.get<{ id: string; estado: string; monto_apertura: number; fecha_apertura: string }>('/api/caja/sesion-activa')
      } catch {
        return null
      }
    },
    refetchInterval: 60_000,
  })
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { profile, rolNivel } = useAuth()
  const { data: dashData } = useDashboard()
  const { data: caja, isLoading: cajaLoading } = useCajaActual()

  const alertas: AlertaRow[] = (dashData?.alertas ?? []).map(a => ({
    id: a.id, nombre: a.nombre, marca: null,
    stock_minimo: Number(a.stock_minimo),
    stockTotal: parseFloat(a.stock_total),
  }))

  const stats = dashData ? {
    sinStock:     alertas.filter(a => a.stockTotal === 0).length,
    stockBajo:    alertas.filter(a => a.stockTotal > 0).length,
    totalAlertas: alertas.length,
  } : undefined

  const ventasHoy = dashData ? {
    items: (dashData.movimientos ?? []).map(m => ({
      id: m.id, numero: m.numero, tipo: m.tipo,
      total: Number(m.total),
      estado: m.estado, created_at: m.created_at,
      clientes: (m.cliente_nombre && m.cliente_nombre !== 'Consumidor final')
        ? { nombre: m.cliente_nombre } : null,
    })) as VentaHoyRow[],
    totalMonto: parseFloat(dashData.ventas.total_ventas_hoy),
    count:      parseInt(dashData.ventas.ventas_hoy),
  } : undefined

  const ccDeuda = dashData ? {
    conDeuda: parseInt(dashData.clientes.con_deuda),
    total:    parseFloat(dashData.clientes.deuda_total),
  } : undefined

  const nombre = profile?.nombre?.split(' ')[0] ?? 'usuario'
  const sinStock = alertas.filter(a => a.stockTotal === 0)
  const bajStock = alertas.filter(a => a.stockTotal > 0)

  return (
    <div>
      {/* ── Greeting ──────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 'var(--me-space-6)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <h1 className="me-page-title">{getSaludo()}, {nombre}</h1>
          <span style={{
            fontFamily: 'var(--me-mono)', fontSize: 12, color: 'var(--me-text-3)',
            textTransform: 'capitalize', letterSpacing: '0.01em',
          }}>
            {formatFechaHoy()}
          </span>
        </div>
        <div style={{ marginTop: 'var(--me-space-1)', fontSize: 13.5, color: 'var(--me-text-3)' }}>
          {stats && stats.totalAlertas > 0
            ? `${stats.totalAlertas} alerta${stats.totalAlertas > 1 ? 's' : ''} de stock pendiente${stats.totalAlertas > 1 ? 's' : ''} — revisá el panel.`
            : ventasHoy
            ? `${ventasHoy.count} venta${ventasHoy.count !== 1 ? 's' : ''} hoy · $${formatMoney(ventasHoy.totalMonto)} facturado.`
            : 'Cargando estado del negocio...'}
        </div>
      </div>

      {/* ── KPIs ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 'var(--me-space-3)', marginBottom: 'var(--me-space-6)' }}>

        <KpiCard
          eyebrow="Ventas hoy"
          value={ventasHoy ? `$${formatMoney(ventasHoy.totalMonto)}` : '—'}
          sub={ventasHoy ? `${ventasHoy.count} operacion${ventasHoy.count !== 1 ? 'es' : ''}` : ''}
          accent="var(--me-ok)"
          urgent={false}
        />

        <KpiCard
          eyebrow="Ticket promedio"
          value={ventasHoy && ventasHoy.count > 0
            ? `$${formatMoney(ventasHoy.totalMonto / ventasHoy.count)}`
            : '—'}
          sub="por venta de hoy"
          accent="var(--me-text-3)"
          urgent={false}
        />

        <KpiCard
          eyebrow="Alertas de stock"
          value={stats ? String(stats.totalAlertas) : '—'}
          sub={stats && stats.totalAlertas > 0
            ? `${stats.sinStock} sin stock · ${stats.stockBajo} bajo min.`
            : 'todo en orden'}
          accent={stats && stats.totalAlertas > 0 ? 'var(--me-bad)' : 'var(--me-ok)'}
          urgent={stats ? stats.totalAlertas > 0 : false}
        />

        <KpiCard
          eyebrow="Deuda cuentas corrientes"
          value={ccDeuda ? `$${formatMoney(ccDeuda.total)}` : '—'}
          sub={ccDeuda ? `${ccDeuda.conDeuda} cliente${ccDeuda.conDeuda !== 1 ? 's' : ''} con saldo` : ''}
          accent={ccDeuda && ccDeuda.total > 0 ? 'var(--me-bad)' : 'var(--me-ok)'}
          urgent={false}
        />

        <CajaKpiCard caja={caja} isLoading={cajaLoading} />
      </div>

      {/* ── Content grid ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 'var(--me-space-4)', alignItems: 'start' }}>

        {/* Left: ventas de hoy */}
        <div className="me-card" style={{ padding: 0, overflow: 'clip' }}>
          <div style={{
            padding: '14px 20px', borderBottom: '1px solid var(--me-line)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>Ventas de hoy</div>
              <div style={{ fontSize: 11, color: 'var(--me-text-4)', marginTop: 1 }}>Últimas 12 operaciones del dia</div>
            </div>
            <Link to="/ventas/historial" style={{ fontSize: 12, color: 'var(--me-text-3)', fontWeight: 600, textDecoration: 'none' }}>
              Ver historial
            </Link>
          </div>

          {!ventasHoy && <VentasSkeleton />}

          {ventasHoy && ventasHoy.items.length === 0 && (
            <div style={{ padding: '44px 24px', textAlign: 'center' }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: 'var(--me-surface-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--me-text-3)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
                </svg>
              </div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--me-text-2)', marginBottom: 4 }}>
                Sin ventas por el momento
              </div>
              <div style={{ fontSize: 12, color: 'var(--me-text-4)', maxWidth: 240, margin: '0 auto' }}>
                Las ventas del dia aparecen aqui en cuanto se registren
              </div>
            </div>
          )}

          {ventasHoy && ventasHoy.items.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--me-line)' }}>
                  {['Hora', 'Venta', 'Cliente', 'Tipo', 'Total'].map((h, i) => (
                    <th key={h} style={{
                      padding: '8px 12px', fontSize: 11, fontWeight: 600,
                      letterSpacing: '0.04em', textTransform: 'uppercase',
                      color: 'var(--me-text-4)', textAlign: i === 4 ? 'right' : 'left',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ventasHoy.items.map((v, idx) => (
                  <VentaRow key={v.id} v={v} even={idx % 2 === 0} />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Right: stock alerts */}
        <div className="me-card" style={{ padding: 0, overflow: 'clip' }}>
          <div style={{
            padding: '14px 20px', borderBottom: '1px solid var(--me-line)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>Alertas stock</span>
              {alertas.length > 0 && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: '50%',
                  background: 'var(--me-bad)', color: '#fff',
                  fontSize: 10, fontWeight: 700, fontFamily: 'var(--me-mono)',
                }}>
                  {alertas.length}
                </span>
              )}
            </div>
            <Link to="/stock" style={{ fontSize: 12, color: 'var(--me-text-3)', fontWeight: 600, textDecoration: 'none' }}>
              Gestionar
            </Link>
          </div>

          {alertas.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center' }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', background: 'var(--me-ok-soft)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px',
              }}>
                <CheckIcon size={16} style={{ color: 'var(--me-ok)' }} />
              </div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--me-text-2)', marginBottom: 4 }}>
                Stock en orden
              </div>
              <div style={{ fontSize: 12, color: 'var(--me-text-4)' }}>
                Todos los productos sobre su minimo
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {sinStock.length > 0 && (
                <div style={{ borderBottom: bajStock.length > 0 ? '1px solid var(--me-line)' : 'none' }}>
                  <div style={{
                    padding: '8px 16px 4px', fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--me-bad)',
                  }}>
                    Sin stock — {sinStock.length}
                  </div>
                  {sinStock.map(a => <AlertItem key={a.id} alerta={a} urgencia="sin" />)}
                </div>
              )}
              {bajStock.length > 0 && (
                <div>
                  <div style={{
                    padding: '8px 16px 4px', fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--me-warn)',
                  }}>
                    Bajo minimo — {bajStock.length}
                  </div>
                  {bajStock.map(a => <AlertItem key={a.id} alerta={a} urgencia="bajo" />)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {rolNivel === 1 && <AdminCharts />}
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function VentaRow({ v, even }: { v: VentaHoyRow; even: boolean }) {
  const estadoColor = v.estado === 'pagada' ? 'var(--me-ok)' : v.estado === 'pago_parcial' ? 'var(--me-warn)' : 'var(--me-text-3)'

  return (
    <tr style={{ background: even ? 'transparent' : 'var(--me-surface-2)' }}>
      <td style={{ padding: '9px 12px', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 11.5, color: 'var(--me-text-3)', whiteSpace: 'nowrap' }}>
        {formatFechaCorta(v.created_at)}<br />
        <span style={{ fontSize: 10.5, color: 'var(--me-text-4)' }}>{formatHora(v.created_at)}</span>
      </td>
      <td style={{ padding: '9px 12px', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontWeight: 600, color: 'var(--me-text-3)', fontSize: 13 }}>
        #{v.numero}
      </td>
      <td style={{ padding: '9px 12px', fontSize: 13, color: 'var(--me-text-2)' }}>
        {v.clientes?.nombre ?? <span style={{ color: 'var(--me-text-4)' }}>—</span>}
      </td>
      <td style={{ padding: '9px 12px' }}>
        {v.tipo === 'mayorista'
          ? <span className="me-pill me-pill-mayor">may</span>
          : <span className="me-pill me-pill-neutral">min</span>}
      </td>
      <td style={{ padding: '9px 12px', textAlign: 'right' }}>
        <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13, fontWeight: 700, color: estadoColor }}>
          ${formatMoney(v.total)}
        </div>
      </td>
    </tr>
  )
}

function VentasSkeleton() {
  return (
    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{ height: 36, borderRadius: 6, background: 'var(--me-surface-3)', opacity: 1 - i * 0.15 }} />
      ))}
    </div>
  )
}

function AlertItem({ alerta, urgencia }: { alerta: AlertaRow; urgencia: 'sin' | 'bajo' }) {
  const borderColor = urgencia === 'sin' ? 'var(--me-bad)' : 'var(--me-warn)'
  const stockColor = urgencia === 'sin' ? 'var(--me-bad)' : 'var(--me-warn)'

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px', borderLeft: `3px solid ${borderColor}`,
        transition: 'background var(--me-dur-micro)',
      }}
      onMouseOver={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--me-surface-2)' }}
      onMouseOut={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontWeight: 500, fontSize: 13, color: 'var(--me-text)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130,
        }}>
          {alerta.nombre}
        </div>
        {alerta.marca && (
          <div style={{ fontSize: 11, color: 'var(--me-text-4)', marginTop: 1 }}>{alerta.marca}</div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 14, fontWeight: 700, color: stockColor }}>
            {alerta.stockTotal}
          </div>
          <div style={{ fontSize: 10, color: 'var(--me-text-4)', fontFamily: 'var(--me-mono)' }}>
            min {alerta.stock_minimo}
          </div>
        </div>
        <Link
          to="/stock"
          style={{
            fontSize: 11, fontWeight: 600, color: 'var(--me-text-3)',
            background: 'var(--me-surface-3)', border: '1px solid var(--me-line)',
            borderRadius: 6, padding: '4px 8px', textDecoration: 'none',
            whiteSpace: 'nowrap', transition: 'all var(--me-dur-micro)',
          }}
          onMouseOver={e => {
            (e.currentTarget as HTMLAnchorElement).style.background = 'var(--me-surface-2)'
            ;(e.currentTarget as HTMLAnchorElement).style.color = 'var(--me-text)'
          }}
          onMouseOut={e => {
            (e.currentTarget as HTMLAnchorElement).style.background = 'var(--me-surface-3)'
            ;(e.currentTarget as HTMLAnchorElement).style.color = 'var(--me-text-3)'
          }}
        >
          Ajustar
        </Link>
      </div>
    </div>
  )
}

function KpiCard({ eyebrow, value, sub, accent, urgent }: {
  eyebrow: string; value: string; sub: string; accent: string; urgent: boolean
}) {
  return (
    <div className="me-card" style={{
      padding: '16px 18px',
      borderTop: `3px solid ${urgent ? accent : 'transparent'}`,
      position: 'relative', overflow: 'hidden',
    }}>
      {urgent && (
        <div style={{
          position: 'absolute', top: 12, right: 12,
          width: 7, height: 7, borderRadius: '50%',
          background: accent, boxShadow: `0 0 0 3px ${accent}33`,
        }} />
      )}
      <div style={{
        fontSize: 10.5, fontWeight: 600, letterSpacing: '0.07em',
        textTransform: 'uppercase', color: 'var(--me-text-4)', marginBottom: 10,
      }}>
        {eyebrow}
      </div>
      <div style={{
        fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
        fontSize: 28, fontWeight: 700,
        color: urgent ? accent : 'var(--me-text)',
        lineHeight: 1, letterSpacing: '-0.02em', marginBottom: 6,
      }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--me-text-4)' }}>{sub}</div>}
    </div>
  )
}

function CajaKpiCard({ caja, isLoading }: {
  caja: { id: string; estado: string; monto_apertura: number; fecha_apertura: string } | null | undefined
  isLoading: boolean
}) {
  const abierta = caja !== null && caja !== undefined

  return (
    <div className="me-card" style={{
      padding: '16px 18px',
      borderTop: `3px solid ${abierta ? 'var(--me-acid)' : 'transparent'}`,
    }}>
      <div style={{
        fontSize: 10.5, fontWeight: 600, letterSpacing: '0.07em',
        textTransform: 'uppercase', color: 'var(--me-text-4)', marginBottom: 10,
      }}>
        Caja
      </div>
      {isLoading ? (
        <div style={{ height: 28, borderRadius: 6, background: 'var(--me-surface-3)', width: 80 }} />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: abierta ? 'var(--me-ok)' : 'var(--me-text-4)',
              boxShadow: abierta ? '0 0 0 3px var(--me-ok-soft)' : 'none',
            }} />
            <span style={{
              fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
              fontSize: 22, fontWeight: 700,
              color: abierta ? 'var(--me-text)' : 'var(--me-text-4)',
              lineHeight: 1, letterSpacing: '-0.02em',
            }}>
              {abierta ? 'ABIERTA' : 'CERRADA'}
            </span>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--me-text-4)' }}>
            {abierta
              ? `Desde las ${formatHora(caja!.fecha_apertura)}`
              : 'Sin sesion activa'}
          </div>
        </>
      )}
    </div>
  )
}
